/**
 * Client side of the compact league wire form (`backend/db/leagues/leagueWire.js`).
 *
 * The server names plays / settlements chunks, row order, members and the Bullion battle by a
 * content hash and skips whatever this client already holds; `hydrateWireLeague` rebuilds the
 * `League` from the response plus the values kept here. Values are kept for the current and the
 * previous response per league, so a poll that raced another fetch can still resolve them.
 */

import type { League, LeagueBullionBattle, LeagueMember, LeaguePlay, LeaguePlayMarketDetails, LeagueSettlement } from "./types";

type WireRow = Record<string, unknown> & { id: string };

interface WireBlob<T> {
    h: string;
    value?: T;
}

interface WireChunk {
    h: string;
    rows?: WireRow[];
}

interface WireRows {
    order: WireBlob<string[]>;
    chunks: WireChunk[];
}

interface WireFeed {
    v: number;
    plays: WireRows;
    settlements: WireRows;
    members: WireBlob<LeagueMember[]>;
    bullion?: WireBlob<LeagueBullionBattle>;
}

export type WireLeague = Omit<League, "plays" | "settlements" | "members" | "bullionBattle"> & { feed: WireFeed };

interface MdRef {
    set?: Record<string, unknown>;
    drop?: string[];
}

interface LeagueWireMemory {
    values: Map<string, unknown>;
    previous: Map<string, unknown>;
    signature: string;
    league: League;
    /** The response with every value filled in — what the persisted cache stores. */
    complete: WireLeague;
}

const memory = new Map<string, LeagueWireMemory>();

export function isWireLeague(row: unknown): row is WireLeague {
    const feed = (row as { feed?: WireFeed } | null)?.feed;
    return Boolean(feed && typeof feed === "object" && Array.isArray(feed.plays?.chunks));
}

/** `{ [leagueId]: hashes }` for `POST /leagues/feed`. */
export function leagueWireHave(): Record<string, string[]> {
    const out: Record<string, string[]> = {};
    for (const [leagueId, mem] of memory) {
        if (mem.values.size > 0) out[leagueId] = [...mem.values.keys()];
    }
    return out;
}

export function resetLeagueWire(leagueId?: string): void {
    if (leagueId) memory.delete(leagueId);
    else memory.clear();
}

/** Complete wire form of `league` when it is the one last hydrated here (for the persisted cache). */
export function completeWireLeague(league: League): WireLeague | null {
    const mem = memory.get(league.id);
    return mem && mem.league === league ? mem.complete : null;
}

function expandMarketDetails(
    ref: MdRef,
    playMd: LeaguePlayMarketDetails | undefined,
): LeaguePlayMarketDetails {
    const md: Record<string, unknown> = { ...(playMd ?? {}) };
    for (const key of ref.drop ?? []) delete md[key];
    return { ...md, ...(ref.set ?? {}) } as LeaguePlayMarketDetails;
}

/**
 * Rebuild a `League` from its wire form, or null when a value it references is no longer held
 * (the caller should refetch without `have`). Leagues without `feed` pass through unchanged.
 * Returns the previously built object when nothing changed, so callers can compare by identity.
 */
export function hydrateWireLeague(row: League | WireLeague): League | null {
    if (!isWireLeague(row)) return row as League;
    const { feed, ...rest } = row;
    const mem = memory.get(row.id);
    const keep = new Map<string, unknown>();
    const hashes: string[] = [];
    let missing = false;
    const take = <T>(part: WireBlob<T>, value: T | undefined): T | undefined => {
        hashes.push(part.h);
        const resolved = (value ?? mem?.values.get(part.h) ?? mem?.previous.get(part.h)) as T | undefined;
        if (resolved === undefined) missing = true;
        else keep.set(part.h, resolved);
        return resolved;
    };
    const rowsOf = (wire: WireRows) => {
        const order = take(wire.order, wire.order.value);
        const chunks = wire.chunks.map((c) => ({ h: c.h, rows: take(c, c.rows) }));
        return { order, chunks };
    };
    const plays = rowsOf(feed.plays);
    const settlements = rowsOf(feed.settlements);
    const members = take(feed.members, feed.members.value);
    const bullion = feed.bullion ? take(feed.bullion, feed.bullion.value) : undefined;
    if (missing) return null;

    const signature = `${JSON.stringify(rest)}|${hashes.join(",")}`;
    const complete: WireLeague = {
        ...rest,
        feed: {
            v: feed.v,
            plays: { order: { h: feed.plays.order.h, value: plays.order }, chunks: plays.chunks },
            settlements: { order: { h: feed.settlements.order.h, value: settlements.order }, chunks: settlements.chunks },
            members: { h: feed.members.h, value: members },
            ...(feed.bullion ? { bullion: { h: feed.bullion.h, value: bullion } } : {}),
        },
    };
    if (mem && mem.signature === signature) {
        memory.set(row.id, { ...mem, values: keep, previous: mem.values, complete });
        return mem.league;
    }

    const assemble = ({ order, chunks }: typeof plays): WireRow[] | null => {
        const byId = new Map<string, WireRow>();
        for (const chunk of chunks) for (const r of chunk.rows ?? []) byId.set(String(r.id), r);
        const out: WireRow[] = [];
        for (const id of order ?? []) {
            const r = byId.get(id);
            if (!r) return null;
            out.push(r);
        }
        return out;
    };
    const playRows = assemble(plays) as unknown as LeaguePlay[] | null;
    const settlementRows = assemble(settlements);
    if (!playRows || !settlementRows) return null;

    const playsById = new Map(playRows.map((p) => [String(p.id), p]));
    const settlementList = settlementRows.map((s) => {
        const { mdRef, ...settlement } = s as WireRow & { mdRef?: MdRef };
        if (!mdRef) return s as unknown as LeagueSettlement;
        const play = playsById.get(String(settlement.playId));
        return {
            ...settlement,
            marketDetails: expandMarketDetails(mdRef, play?.marketDetails),
        } as unknown as LeagueSettlement;
    });

    const league = {
        ...rest,
        members,
        plays: playRows,
        settlements: settlementList,
        ...(bullion
            ? { bullionBattle: bullion }
            : !feed.bullion && mem?.league.bullionBattle
              ? { bullionBattle: mem.league.bullionBattle }
              : {}),
    } as League;
    memory.set(row.id, { values: keep, previous: mem?.values ?? new Map(), signature, league, complete });
    return league;
}
